import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import I from "./Icons";
import { pick } from "../lib/i18n";

type Props = {
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (instruction: string) => Promise<void>;
};

const MAX_INSTRUCTION_LENGTH = 500;

const copy = {
  pl: {
    title: "Popraw wynik",
    intro: "Podaj jedną konkretną zmianę do kolejnej wersji tej grafiki.",
    label: "Instrukcja korekty",
    hint: "Np. „zachowaj kadr, popraw kształt uchwytu zgodnie ze zdjęciem produktu”.",
    source: "Zdjęcia produktu pozostają źródłem konstrukcji. Ten wynik trafi do modelu tylko jako referencja korekty.",
    cancel: "Anuluj",
    submit: "Wygeneruj z korektą",
    submitting: "Przygotowuję…",
    required: "Wpisz instrukcję korekty.",
    close: "Zamknij",
  },
  en: {
    title: "Correct result",
    intro: "Give one specific change for the next version of this visual.",
    label: "Correction instruction",
    hint: "For example: “keep the framing and correct the handle shape using the product photo”.",
    source: "The product photos remain the construction source. This result is sent to the model only as a correction reference.",
    cancel: "Cancel",
    submit: "Generate with correction",
    submitting: "Preparing…",
    required: "Enter a correction instruction.",
    close: "Close",
  },
};

export default function ManualCorrectionDialog({ submitting, error, onClose, onSubmit }: Props) {
  const t = pick(copy);
  const [instruction, setInstruction] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const errorMessage = validationError ?? error;
  const helperId = "manual-correction-help";
  const errorId = "manual-correction-error";

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    dialog.showModal();
    inputRef.current?.focus();

    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedInstruction = instruction.trim();
    if (!normalizedInstruction) {
      setValidationError(t.required);
      return;
    }

    setValidationError(null);
    await onSubmit(normalizedInstruction);
  };

  const handleCancel = () => {
    if (!submitting) onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="manual-correction-title"
      onCancel={(event) => {
        event.preventDefault();
        handleCancel();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) handleCancel();
      }}
      style={{
        width: "min(92vw, 36rem)",
        margin: "auto",
        padding: 0,
        border: "1px solid var(--line, var(--border))",
        borderRadius: 12,
        background: "var(--surface, var(--panel))",
        color: "var(--ink)",
        boxShadow: "0 24px 80px oklch(0 0 0 / .35)",
      }}
    >
      <form onSubmit={handleSubmit} style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <h2 id="manual-correction-title" style={{ margin: 0, fontSize: 18 }}>{t.title}</h2>
            <p style={{ margin: "6px 0 0", color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}>{t.intro}</p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" aria-label={t.close} onClick={handleCancel} disabled={submitting}>
            {I.x}
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label htmlFor="manual-correction-instruction" style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</label>
          <textarea
            ref={inputRef}
            id="manual-correction-instruction"
            value={instruction}
            onChange={(event) => {
              setInstruction(event.target.value);
              setValidationError(null);
            }}
            maxLength={MAX_INSTRUCTION_LENGTH}
            rows={4}
            disabled={submitting}
            aria-invalid={Boolean(errorMessage)}
            aria-describedby={errorMessage ? `${helperId} ${errorId}` : helperId}
            style={{ width: "100%", resize: "vertical", minHeight: 96, boxSizing: "border-box" }}
          />
          <p id={helperId} style={{ margin: 0, color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>{t.hint}</p>
          {errorMessage && <p id={errorId} role="alert" style={{ margin: 0, color: "var(--danger, #c0392b)", fontSize: 12 }}>{errorMessage}</p>}
        </div>

        <p style={{ margin: 0, padding: 12, borderRadius: 8, background: "var(--bg-2)", color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
          {t.source}
        </p>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={handleCancel} disabled={submitting}>{t.cancel}</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? t.submitting : t.submit}
          </button>
        </div>
      </form>
    </dialog>
  );
}
